import { jwtDecode } from 'jwt-decode';
import 'navigator.locks';
const hasWindow = typeof window !== 'undefined';
/**
 * Wait for a certain duration. Async wrapper around setTimeout.
 * @param ms Duration in milliseconds
 * @returns Promise that resolves to void after ms milliseconds
 */
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
export class AuthError extends Error {
    constructor(message, context) {
        super(message);
        this.name = 'AuthError';
        this.context = context;
    }
}
export class Auth {
    /**
     *
     * @param host - The hostname of the API you want to use. This must be same-site with the current origin (i.e cookies considered first-party).
     * @param callback - The callback URL after a successful login.
     * @param autologin - If true, the user will automatically be redirected if the login state resolves as unauthenticated.
     * @param persistentImpersonation - Store impersonation state in LocalStorage to persist between page reloads. Has no effect outside a browser context.
     */
    constructor(host = 'https://auth.opoint.com', callback, options) {
        var _a, _b, _c, _d, _e;
        this.host = host;
        this.callback = callback;
        this.options = {
            autologin: (_a = options === null || options === void 0 ? void 0 : options.autologin) !== null && _a !== void 0 ? _a : true,
            exponentialBackoff: (_b = options === null || options === void 0 ? void 0 : options.exponentialBackoff) !== null && _b !== void 0 ? _b : 10,
            persistentImpersonation: ((_c = options === null || options === void 0 ? void 0 : options.persistentImpersonation) !== null && _c !== void 0 ? _c : false) && hasWindow,
            throw400s: (_d = options === null || options === void 0 ? void 0 : options.throw400s) !== null && _d !== void 0 ? _d : false,
        };
        this.state = undefined;
        this.impersonation_privileges_index = 0;
        if (this.options.persistentImpersonation) {
            const persisted = JSON.parse((_e = window.localStorage.getItem('opoint-authjs-impersonation')) !== null && _e !== void 0 ? _e : '{}');
            if (persisted.impersonating)
                this.impersonating = persisted.impersonating;
            if (persisted.impersonation_privileges_index)
                this.impersonation_privileges_index = persisted.impersonation_privileges_index;
        }
        else {
            hasWindow && window.localStorage.removeItem('opoint-authjs-impersonation');
        }
    }
    /**
     * Redirect the page to the login page, using the provided API URL and Callback.
     * Only works in a browser context.
     * @param redirect - Path added to the end of the redirect given in the constructor.
     */
    login(redirect = undefined) {
        var _a, _b, _c;
        window.localStorage.removeItem('opoint-authjs-impersonation');
        const callbackUrl = new URL(this.callback);
        window.location.replace(`${this.host}/api/v1/login/browser?redirect=${this.callback}${redirect !== null && redirect !== void 0 ? redirect : (this.options.autologin &&
            callbackUrl.pathname === '/' &&
            callbackUrl.search === '' &&
            callbackUrl.hash === ''
            ? ((_a = window.location.pathname) !== null && _a !== void 0 ? _a : '') +
                ((_b = window.location.search) !== null && _b !== void 0 ? _b : '') +
                ((_c = window.location.hash) !== null && _c !== void 0 ? _c : '')
            : '')}`);
    }
    /**
     * Impersonate a user
     * @param id the user to impersonate, or undefined to stop impersonating anyone.
     * @param privileges_index the privileges index to impersonate with. Does not affect the main session.
     * @returns the new user object after impersonation
     */
    async impersonate(id, privileges_index) {
        await navigator.locks.request('authjs', async () => {
            this.impersonating = id;
            if (privileges_index != undefined)
                this.impersonation_privileges_index = privileges_index;
            await this.fetchToken();
            if (this.options.persistentImpersonation) {
                window.localStorage.setItem('opoint-authjs-impersonation', JSON.stringify({
                    impersonating: this.impersonating,
                    impersonation_privileges_index: this.impersonation_privileges_index,
                }));
            }
        });
        return this.state ? this.state.user : this.state;
    }
    /**
     * Logs the user out and clears the login state from this object.
     * @returns true if logout successful, false otherwise.
     */
    async logout() {
        let res = false;
        await navigator.locks.request('authjs', async () => {
            try {
                const result = await fetch(this.host + '/api/v1/logout', {
                    method: 'POST',
                    credentials: 'include',
                });
                if (result.ok) {
                    this.state = null;
                    res = true;
                    window.localStorage.removeItem('opoint-authjs-impersonation');
                }
                else {
                    res = false;
                }
            }
            catch {
                res = false;
            }
        });
        if (this.options.autologin)
            this.login();
        return res;
    }
    /**
     * Check if the current user can impersonate the given user.
     * Mirrors functionality in the API, but it might be convenient to have it here.
     * @param target id if the user you want to impersonate
     * @returns true or false depending on if the user can impersonate the target
     */
    async canImpersonate(target) {
        let res = false;
        await navigator.locks.request('authjs', async () => {
            await navigator.locks.request('authjs-request', async () => {
                const result = await fetch(this.host + '/api/v1/can-impersonate?user=' + target, {
                    credentials: 'include',
                });
                if (result.ok) {
                    res = true;
                }
            });
        });
        return res;
    }
    /**
     * Fetch the token and user state from the API.
     * Is self-waiting, meaning concurrent calls while waiting for network response will read from the same response.
     * Will never send concurrent network calls to the API (that could cause session drops as refresh tokens are reused).
     * @returns Promise which resolves to void when the user is fetched.
     */
    async fetchToken(depth = 0) {
        var _a, _b, _c;
        try {
            const response = await fetch(this.host +
                `/api/v1/get-token${this.impersonating
                    ? '?impersonating=' +
                        this.impersonating +
                        '&privileges_index=' +
                        ((_c = (_a = this.impersonation_privileges_index) !== null && _a !== void 0 ? _a : (_b = this.state) === null || _b === void 0 ? void 0 : _b.user.privileges_index) !== null && _c !== void 0 ? _c : 0)
                    : ''}`, { credentials: 'include', keepalive: true });
            if (response.ok) {
                const data = await response.json();
                const tokenData = jwtDecode(data.token);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { iat, iss, exp, ...userData } = tokenData;
                this.state = {
                    token: {
                        payload: data.token,
                        expiry: new Date(Date.now() + (exp - iat) * 1000), // Gracefully handle clock offsets
                    },
                    user: userData,
                    impersonating: this.impersonating,
                };
                return;
            }
            if (this.options.autologin) {
                this.login();
            }
            else {
                if (response.status >= 500)
                    throw new AuthError('Server error', response);
                if (this.options.throw400s) {
                    if (response.status === 404)
                        throw new AuthError('No session found', response);
                    if (response.status >= 400)
                        throw new AuthError('Client error', response);
                }
                this.state = null;
            }
        }
        catch (e) {
            // Only catch network errors (i.e "fetch failed")
            // and server (5xx) errors
            if (!(e instanceof TypeError || (e instanceof AuthError && e.context.status >= 500))) {
                throw e;
            }
            if (depth > 10) {
                // We can't keep trying forever.
                // This represents at most around 10s of trying.
                throw e;
            }
            // Do some exponential backoff in 10ms base increments.
            await sleep(2 ** depth * this.options.exponentialBackoff);
            await this.fetchToken(depth + 1);
        }
    }
    /**
     * Gets the current AuthState. Will fetch tokens if uninitialised or if token is nearing expiry.
     * Always definitely resolves the authentication state of the user,
     * with the caveat that an auth state is considered valid so long as the token is valid.
     * @param noCache Whether to force fetching a fresh token. Only use when necessary.
     * @returns the current AuthState after fetching if necessary.
     */
    async getState(noCache) {
        // After this mutex block, the state is guaranteed to be upd to date
        // Most of the time---i.e when the token is valid---this is three simple comparisons and a no-op,
        // so the lock is held for a very short time.
        await navigator.locks.request('authjs', async () => {
            if (!this.state || noCache) {
                await this.fetchToken();
            }
            else if (this.state.token.expiry.getTime() - 30 * 1000 < new Date().getTime()) {
                // Less than 30 seconds to token expiry, better just treat it as expired to avoid a 401.
                await this.fetchToken();
            }
        });
        return this.state; // this.fetchToken() will _always_ leave state as AuthState | null.
    }
    /**
     * Gets the current Token. Will fetch tokens if uninitialised or if token is nearing expiry.
     * @param noCache Whether to force fetching a fresh token. Only use when necessary.
     * @returns the current Token after fetching if necessary.
     */
    async getToken(noCache) {
        const state = await this.getState(noCache);
        if (!state)
            return state;
        return state.token;
    }
    /**
     * Gets the current Token payload. Will fetch tokens if uninitialised or if token is nearing expiry.
     * @param noCache Whether to force fetching a fresh token. Only use when necessary.
     * @returns the current Token payload after fetching if necessary.
     */
    async getTokenString(noCache) {
        const state = await this.getState(noCache);
        if (!state)
            return state;
        return state.token.payload;
    }
    /**
     * Gets the current OpointUser. Will fetch tokens if uninitialised or if token is nearing expiry.
     * @param noCache Whether to force fetching a fresh token. Only use when necessary.
     * @returns the current OpointUser after fetching if necessary.
     */
    async getUser(noCache) {
        const state = await this.getState(noCache);
        if (!state)
            return state;
        return state.user;
    }
}
//# sourceMappingURL=auth.js.map